import {
  Box,
  Chip,
  Grid,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import makeStyles from '@mui/styles/makeStyles';
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import service from "../Helpers/gifservices";
import { setData, setServiceFunction } from "../../../store/formSlice";
import {
  canEditSelector,
  refereeSelector,
  refereeStatusSelector,
  setRefereeStatus,
} from "../../../store/refereeSlice";
import { useDispatch, useSelector } from "react-redux";
import { Recommendations } from "../Constants";
import { useLoading } from "../../../providers/loadingProvider";
import { checkFinalRecommendation } from "../Helpers/common";

const pageName = "Overall Recommendation";

const useStyles = makeStyles((theme) => ({
  recommedChip: {
    marginLeft: 20,
    width: 181,
    height: 61,
    fontSize: 18,
    fontWeight: 500,
    cursor: "pointer",
    backgroundColor: "#D4DDF8",
    color: theme.palette.gif.blue.color,
    "&:hover": {
      backgroundColor: theme.palette.gif.blue.hover,
    },
    borderRadius: 8,
  },
  selectedChip: {
    cursor: "context-menu",
    color: "#FFFFFF",
    backgroundColor: theme.palette.gif.blue.color,
    "&:hover": {
      backgroundColor: theme.palette.gif.blue.color,
    },
  },
  title: {
    fontWeight: 500,
  },
  infoIcon: {
    position: "relative",
    top: 6,
    marginLeft: 5,
  },
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: "#fff",
  },
}));

const FinalRecommendation = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const loading = useLoading();
  const referee = useSelector(refereeSelector);
  const refereeStatus = useSelector(refereeStatusSelector);
  const canEdit = useSelector(canEditSelector);
  const [proposalRefereeId, setProposalRefereeId] = useState(null);
  const [proposalReferee, setProposalReferee] = useState({});
  const [noteForAdvisors, setNoteForAdvisors] = useState("");
  const [finalRecommendationValue, setFinalRecommendationValue] = useState(0);

  useEffect(() => {
    setProposalRefereeId(referee?.id);
  }, [referee]);

  const onClick = (value) => {
    setFinalRecommendationValue(value);
    const newpr = {
      ...proposalReferee,
      refereeFinalRecommend: value,
    };
    setProposalReferee(newpr);
    dispatch(
      setData({
        proposalReferee: newpr,
      })
    );
  };

  const GetProposalReferee = useCallback(async () => {
    if (!proposalRefereeId) return;
    const loadingId = loading.addLoading("Loading Proposal");
    var proposalreferee = await service.GetProposalRefereeById({
      proposalRefereeId,
    });

    setProposalReferee(proposalreferee);
    const finalRecommendationType = proposalreferee.refereeFinalRecommend;
    setFinalRecommendationValue(finalRecommendationType);
    setNoteForAdvisors(proposalreferee.noteForAdvisors);
    loading.removeLoading(loadingId);
  }, [proposalRefereeId, loading]);

  const noteForAdvisorsChange = (event) => {
    setNoteForAdvisors(event.target.value);
    const newpr = {
      ...proposalReferee,
      noteForAdvisors: event.target.value,
    };
    setProposalReferee(newpr);
    dispatch(
      setData({
        proposalReferee: newpr,
      })
    );
  };

  useEffect(() => {
    dispatch(setServiceFunction(`UpdateRefereeRecommendation`));
  }, [dispatch]);

  useEffect(() => {
    GetProposalReferee();
  }, [proposalRefereeId, GetProposalReferee]);

  useEffect(() => {
    return () => {
      if (!proposalReferee) return;
      if (
        proposalReferee.refereeFinalRecommend !== finalRecommendationValue ||
        proposalReferee.noteForAdvisors !== noteForAdvisors
      ) {
        const newProposalReferee = {
          ...proposalReferee,
          refereeFinalRecommend: finalRecommendationValue,
          noteForAdvisors:
            noteForAdvisors !== ""
              ? noteForAdvisors
              : proposalReferee.noteForAdvisors,
        };

        const finalRecommendStatus =
          checkFinalRecommendation(newProposalReferee);
        if (refereeStatus.finalRecommendation !== finalRecommendStatus)
          dispatch(
            setRefereeStatus({
              ...refereeStatus,
              finalRecommendation: finalRecommendStatus,
            })
          );
      }
    };
  }, [
    proposalReferee,
    dispatch,
    finalRecommendationValue,
    noteForAdvisors,
    refereeStatus,
  ]);

  return (
    <>
      <Grid container>
        <Typography variant="subtitle2">{pageName}</Typography>
      </Grid>
      <Box marginTop={4} padding={1}>
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <Typography variant="subtitle1">
              Select one of the following to reflect on the quality of the
              overall application
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Box>
              {Recommendations.map((r, index) => (
                <Tooltip
                  key={r.label}
                  title={<Typography>{r.tooltip}</Typography>}
                >
                  <Chip
                    disabled={!canEdit}
                    key={r.label}
                    label={r.label}
                    className={
                      r.value === finalRecommendationValue
                        ? `${classes.selectedChip} ${classes.recommedChip}`
                        : `${classes.recommedChip}`
                    }
                    onClick={() => onClick(r.value)}
                    clickable={false}
                  ></Chip>
                </Tooltip>
              ))}
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box marginTop={10}>
              <Typography style={{ display: "inline" }}>
                Here you may write a special note to the GIF advisors
              </Typography>
              <InfoOutlinedIcon color="primary" className={classes.infoIcon} />
              <TextField
                disabled={!canEdit}
                style={{ marginTop: 10 }}
                multiline
                value={noteForAdvisors || ""}
                onChange={noteForAdvisorsChange}
                label="i think that..."
                fullWidth={true}
                rows={6}
              />
            </Box>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default FinalRecommendation;
